import React from 'react';
import { Box, Button, Chip, Stack, Typography, useTheme } from '@mui/material';

import { ShimmerButton } from '@/components/ShimmerButton';
import logo from '@/assets/NoteGoLogo_Horizontal.png';
import onboardingArrowSvg from '@/assets/onboarding-arrow.svg';

type TrialWelcomeHeaderProps = {
  isLargeDesktop: boolean;
  canCreateNote: boolean;
  trialDays: number;
  onCreateNote: () => void;
  onUpgrade: () => void;
};

const TrialWelcomeHeader: React.FC<TrialWelcomeHeaderProps> = ({
  isLargeDesktop,
  canCreateNote,
  trialDays,
  onCreateNote,
  onUpgrade,
}) => {
  const theme = useTheme();

  return (
    <Stack
      spacing={{ xs: 2, sm: 2.5, md: 3 }}
      alignItems="center"
      textAlign="center"
      sx={{ position: 'relative', width: '100%' }}
    >
      {isLargeDesktop && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 10,
          }}
        >
          {canCreateNote ? (
            <Box position="relative" display="inline-block">
              <ShimmerButton
                variant="contained"
                color="primary"
                size="large"
                onClick={onCreateNote}
                sx={{
                  minHeight: '48px',
                  px: 3,
                  py: 1.5,
                }}
              >
                Create Your First Note
              </ShimmerButton>
              <Box
                position="absolute"
                top="100%"
                left="40%"
                sx={{
                  transform: 'translateX(-50%) rotate(180deg)',
                  mt: 1,
                  zIndex: 1000,
                  display: { xs: 'none', lg: 'block' },
                }}
              >
                <img
                  src={onboardingArrowSvg}
                  alt="Arrow pointing to button"
                  style={{ width: '100px', height: 'auto', opacity: 0.7 }}
                />
              </Box>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={onUpgrade}
              sx={{
                minHeight: '48px',
                px: 3,
                py: 1.5,
              }}
            >
              Upgrade for more
            </Button>
          )}
        </Box>
      )}

      <Stack spacing={2} alignItems="center">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1, sm: 1.2, md: 1.5 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.75rem', sm: '2.125rem', md: '3rem' },
            }}
          >
            Welcome to
          </Typography>
          <Box
            component="img"
            src={logo}
            alt="NoteGo"
            sx={{
              height: { xs: '36px', sm: '42px', md: '48px' },
              width: 'auto',
              objectFit: 'contain',
              verticalAlign: 'middle',
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.75rem', sm: '2.125rem', md: '3rem' },
            }}
          >
            Pro!
          </Typography>
        </Box>

        <Typography
          variant="h6"
          sx={{
            color: theme.palette.neutral.greyFont,
            maxWidth: { xs: '100%', sm: 600, md: 700 },
            fontWeight: 400,
            fontSize: { xs: '1rem', md: '1.25rem' },
            lineHeight: { xs: 1.5, md: 1.6 },
            px: { xs: 1, sm: 2, md: 0 },
          }}
        >
          You've been upgraded to a {trialDays}-day free trial of our Pro plan. Experience unlimited possibilities and
          unlock the full potential of your creativity.
        </Typography>

        <Chip
          label={`${trialDays} DAYS FREE TRIAL ACTIVE`}
          sx={{
            mt: 1,
            px: { xs: 1.5, md: 2 },
            py: { xs: 2, md: 2.5 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            fontWeight: 600,
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
            color: 'white',
            border: 'none',
            borderRadius: '100px',
            letterSpacing: '0.025em',
            minHeight: '44px',
          }}
        />
      </Stack>

      {!isLargeDesktop && (
        <Box sx={{ mt: { xs: 2, md: 2.5 }, width: '100%', display: 'flex', justifyContent: 'center' }}>
          {canCreateNote ? (
            <ShimmerButton
              variant="contained"
              color="primary"
              size="large"
              onClick={onCreateNote}
              sx={{
                minHeight: '48px',
                px: 3,
                py: 1.5,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                maxWidth: { xs: '280px', sm: '300px', md: '350px' },
                width: { xs: '100%', md: 'auto' },
                minWidth: { xs: 'auto', md: '250px' },
              }}
            >
              Create Your First Note
            </ShimmerButton>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={onUpgrade}
              sx={{
                minHeight: '48px',
                px: 3,
                py: 1.5,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                maxWidth: { xs: '280px', sm: '300px', md: '350px' },
                width: { xs: '100%', md: 'auto' },
                minWidth: { xs: 'auto', md: '250px' },
              }}
            >
              Upgrade for more
            </Button>
          )}
        </Box>
      )}
    </Stack>
  );
};

export default TrialWelcomeHeader;
